/**
 * Open an approved Partner CUI account in this window's isolated cookie box.
 * A serialized, verified login sequence replaces the old event-only chain:
 * dom-ready can fire while loadURL is pending, and must not lose the next step.
 * The actual window rechecks identity on navigation and every minute. Capture
 * clients renew their machine identity; only Rafael's own session requests his ticket.
 */
import { useCallback, useEffect, useState } from 'react';
import { callInPanel, type WebviewHandle } from './PartnerSessionPanel';
import { inPartnerBox, meldePartnerKontoAn, PartnerfensterNichtGeladen } from './partnerKontoAnmeldung';
export { passtZuFreigabe } from './partnerKontoAnmeldung';

interface Freigabe { kuerzel: string; label: string; email: string; app?: string }
interface PartnerKontoAutoLoginProps {
  konto: string;
  /** Stable recipe destination, never a transient ticket URL. */
  url: string;
  partition: string;
  aufnahme?: boolean;
  webviewRef: React.RefObject<HTMLWebViewElement | null>;
}

export default function PartnerKontoAutoLogin({ konto, url, partition, aufnahme = false, webviewRef }: PartnerKontoAutoLoginProps) {
  const [freigabe, setFreigabe] = useState<Freigabe | null | 'unbekannt'>(null);
  const [status, setStatus] = useState('Wird vorbereitet …');
  const [offen, setOffen] = useState(false);
  const [fehlgeschlagen, setFehlgeschlagen] = useState(false);
  const [neustart, setNeustart] = useState(0);

  useEffect(() => {
    let abgebrochen = false;
    setFreigabe(null);
    fetch('/api/guide/konten')
      .then(r => { if (!r.ok) throw new Error(`Kontofreigabe nicht lesbar — HTTP ${r.status}`); return r.json(); })
      .then((d: { konten?: Freigabe[] }) => {
        if (!abgebrochen) setFreigabe((d.konten ?? []).find(k => k.kuerzel === konto) ?? 'unbekannt');
      })
      .catch(e => {
        if (abgebrochen) return;
        setStatus(e.message); setFehlgeschlagen(true); setOffen(true);
      });
    return () => { abgebrochen = true; };
  }, [konto, neustart]);

  useEffect(() => {
    if (!freigabe) return;
    const fehler = (text: string) => { setStatus(text); setFehlgeschlagen(true); setOffen(true); };
    if (freigabe === 'unbekannt') { fehler(`Konto "${konto}" steht nicht in der Freigabeliste — nicht angemeldet.`); return; }
    if (freigabe.app !== 'partner-cui') { fehler(`Konto "${konto}" ist kein freigegebenes Partner-CUI-Konto.`); return; }
    const wv = webviewRef.current;
    if (!wv) return;
    let aktiv = true;
    let busy = false;
    let erstmals = true;
    let nochmalNoetig = false;
    // Nach einem Navigationsfehler wird nicht gefragt, bis die Webview wieder ein echtes
    // Dokument committet (did-navigate kommt nur fuer Commits ohne Fehlerseite).
    let wartetAufNavigation = false;
    // Zaehlt echte Commits (did-navigate). Kommt einer, waehrend pruefen() noch das Fehlerdokument
    // las (z. B. automatisches Nachladen des BrowserPanels), darf der catch nicht mehr pausieren.
    let navigationen = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const herkunft = async (): Promise<string> => {
      const browser = wv as unknown as WebviewHandle;
      if (!browser?.executeJavaScript) return '';
      try {
        const href = await Promise.race([
          browser.executeJavaScript('location.href'),
          new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 3000)),
        ]);
        return String(href ?? '');
      } catch {
        return ''; // silent-ok: kein lesbares Dokument = 'leer'; meldePartnerKontoAn wartet dann sichtbar ("Warte auf das Partnerfenster")
      }
    };
    const pruefen = async () => {
      if (!aktiv || wartetAufNavigation) return;
      if (busy) { nochmalNoetig = true; return; }
      busy = true;
      clearTimeout(timer);
      const navigationenBeiStart = navigationen;
      let gelungen = false;
      let ueberholt = false;
      try {
        const ok = await inPartnerBox(partition, () => meldePartnerKontoAn({
          email: freigabe.email, label: freigabe.label, ziel: url, aktualisieren: erstmals,
          aktiv: () => aktiv,
          herkunft,
          status: text => { if (aktiv) setStatus(text); },
          call: (path, init) => callInPanel(wv as unknown as WebviewHandle, path, init),
          navigate: async target => {
            const browser = wv as unknown as WebviewHandle;
            if (!browser?.loadURL) throw new Error('Partnerfenster ist noch nicht bereit.');
            await browser.loadURL(target);
          },
          ticket: async () => {
            const ziel = new URL(url);
            const query = new URLSearchParams({ ziel: ziel.pathname + ziel.search });
            if (aufnahme) query.set('als', 'aufnahme');
            const r = await fetch(`/api/fern-guide/ziel?${query}`);
            const d = await r.json();
            if (!r.ok) throw new Error(d.grund || d.error || `Ticket nicht erhalten — HTTP ${r.status}`);
            if (!d.url) throw new Error('Ticket-Antwort ohne Adresse.');
            const adresse = new URL(d.url);
            if (adresse.origin !== ziel.origin || adresse.pathname !== '/api/fern-guide/anmelden') {
              throw new Error('Anmeldeticket gehört nicht zur Partnerplattform dieser Kachel.');
            }
            return d.url;
          },
        }));
        if (aktiv && ok) { erstmals = false; gelungen = true; setFehlgeschlagen(false); timer = setTimeout(pruefen, 60_000); }
      } catch (e) {
        ueberholt = e instanceof PartnerfensterNichtGeladen && navigationen !== navigationenBeiStart;
        if (e instanceof PartnerfensterNichtGeladen && !ueberholt) { wartetAufNavigation = true; clearTimeout(timer); }
        // Ueberholt: inzwischen ist ein echtes Dokument da, finally prueft sofort neu statt den alten Fehler zu zeigen.
        if (aktiv && !ueberholt) fehler(e instanceof Error ? e.message : String(e));
      } finally {
        busy = false;
        if (aktiv && (gelungen || ueberholt) && nochmalNoetig) { clearTimeout(timer); timer = setTimeout(pruefen, 0); }
        nochmalNoetig = false;
      }
    };
    const takt = () => { void pruefen(); };
    const navigiert = () => { navigationen += 1; wartetAufNavigation = false; void pruefen(); };
    wv.addEventListener('dom-ready', takt);
    wv.addEventListener('did-navigate', navigiert);
    void pruefen();
    return () => {
      aktiv = false;
      clearTimeout(timer);
      wv.removeEventListener('dom-ready', takt);
      wv.removeEventListener('did-navigate', navigiert);
    };
  }, [freigabe, konto, url, partition, aufnahme, webviewRef]);

  const nochmal = useCallback(() => { setFehlgeschlagen(false); setNeustart(n => n + 1); }, []);

  const label = typeof freigabe === 'object' && freigabe ? freigabe.label : konto;

  if (!offen) {
    return (
      <button
        onClick={() => setOffen(true)}
        title={status}
        style={{
          position: 'absolute', top: 44, right: 0, zIndex: 20,
          background: fehlgeschlagen ? 'var(--tnfh-f7768e)' : 'var(--tn-bg-dark)',
          color: fehlgeschlagen ? 'var(--tnf-1a1b26)' : 'var(--tn-text)',
          border: '1px solid var(--tn-border)', borderRight: 'none',
          borderRadius: '6px 0 0 6px', padding: '8px 6px', cursor: 'pointer',
          fontSize: 11, writingMode: 'vertical-rl',
        }}
      >
        {fehlgeschlagen ? '⚠' : '🪪'} {label}
      </button>
    );
  }

  return (
    <div
      style={{
        position: 'absolute', top: 38, right: 0, width: 280, zIndex: 20,
        display: 'flex', flexDirection: 'column', gap: 6,
        background: 'var(--tn-surface)', borderLeft: '1px solid var(--tn-border)',
        boxShadow: '-4px 0 12px rgba(0,0,0,0.3)', padding: 8,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--tn-text)' }}>🪪 Konto-Anmeldung</span>
        <button
          onClick={() => setOffen(false)}
          style={{ background: 'none', border: 'none', color: 'var(--tn-text-muted)', cursor: 'pointer', fontSize: 14 }}
        >×</button>
      </div>
      <div style={{ fontSize: 11, color: 'var(--tn-text)', lineHeight: 1.4 }}>{status}</div>
      {fehlgeschlagen && (
        <button
          onClick={nochmal}
          style={{
            background: 'var(--tn-blue, var(--tnfh-7aa2f7))', color: 'var(--tnf-1a1b26)', border: 'none',
            borderRadius: 4, padding: '5px 8px', fontSize: 11, fontWeight: 600, cursor: 'pointer',
          }}
        >
          Erneut versuchen
        </button>
      )}
    </div>
  );
}
